import React, { useEffect, useState, useCallback } from 'react';
import { BarChart2, Download, RefreshCw } from 'lucide-react';
import apiClient from '../../utils/api';

const COMPANIES_LIMIT = 200;

export const SuperAdminReports: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ companyId: '', status: '', dateFrom: '', dateTo: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.companyId) params.companyId = filters.companyId;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      const res = await apiClient.getReportStats(params);
      setData(res.data.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    apiClient.listCompanies(COMPANIES_LIMIT, 0).then(r => {
      const d = r.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      const res = await apiClient.exportShipmentsCsv(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipments-report-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {} finally { setExporting(false); }
  };

  const shipmentStats: any[] = data?.shipmentStats ?? [];
  const paymentStats: any[] = data?.paymentStats ?? [];
  const dailyTrend: any[] = data?.dailyTrend ?? [];
  const topRoutes: any[] = data?.topRoutes ?? [];

  const totalShipments = shipmentStats.reduce((s, r) => s + parseInt(r.count), 0);
  const totalRevenue = paymentStats.filter(p => p.status === 'COMPLETED').reduce((s, r) => s + parseFloat(r.total || 0), 0);
  const maxDaily = Math.max(...dailyTrend.map(d => parseInt(d.count)), 1);

  const STATUS_COLORS: Record<string, string> = {
    DELIVERED: '#22c55e', IN_TRANSIT: '#0ea5e9', DEPARTED: '#6366f1',
    CONFIRMED: '#f59e0b', PENDING: '#94a3b8', CANCELLED: '#ef4444', RETURNED: '#f97316',
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart2 size={20} /> Reports & Analytics
        </h1>
        <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
          <Download size={15} /> {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Company</label>
            <select value={filters.companyId} onChange={e => setFilters(f => ({ ...f, companyId: e.target.value }))} style={{ width: 200 }}>
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Status</label>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={{ width: 160 }}>
              {['', 'PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'RETURNED', 'CANCELLED'].map(s =>
                <option key={s} value={s}>{s || 'All Statuses'}</option>
              )}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>From</label>
            <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>To</label>
            <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
          </div>
          <button className="btn btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> {loading ? 'Loading...' : 'Apply'}
          </button>
          <button className="btn btn-ghost" onClick={() => setFilters({ companyId: '', status: '', dateFrom: '', dateTo: '' })}>
            Clear
          </button>
        </div>
      </div>

      {loading ? <div className="loading-spinner">Loading report data...</div> : (
        <>
          {/* Summary cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
            {[
              { label: 'Total Shipments', value: totalShipments, color: '#3b82f6' },
              { label: 'Delivered', value: shipmentStats.find(s => s.status === 'DELIVERED')?.count ?? 0, color: '#22c55e' },
              { label: 'Cancelled', value: shipmentStats.find(s => s.status === 'CANCELLED')?.count ?? 0, color: '#ef4444' },
              { label: 'Revenue (RWF)', value: totalRevenue.toLocaleString(), color: '#10b981' },
            ].map(c => (
              <div className="stat-card" key={c.label} style={{ borderLeft: `4px solid ${c.color}` }}>
                <div className="stat-label">{c.label}</div>
                <div className="stat-value" style={{ color: c.color, fontSize: typeof c.value === 'string' && c.value.length > 8 ? 18 : 28 }}>{c.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            {/* Shipment status breakdown */}
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Shipments by Status</h2>
              {shipmentStats.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No data</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {shipmentStats.map((s: any) => {
                    const pct = totalShipments > 0 ? Math.round((parseInt(s.count) / totalShipments) * 100) : 0;
                    return (
                      <div key={s.status}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                          <span style={{ fontWeight: 500 }}>{s.status}</span>
                          <span style={{ color: '#64748b' }}>{s.count} ({pct}%)</span>
                        </div>
                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: STATUS_COLORS[s.status] || '#94a3b8', borderRadius: 4, transition: 'width 0.4s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payment method breakdown */}
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Revenue by Payment Method</h2>
              {paymentStats.filter(p => p.status === 'COMPLETED').length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 13 }}>No completed payments</div>
              ) : (
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Method</th><th>Count</th><th>Total (RWF)</th></tr></thead>
                    <tbody>
                      {paymentStats.filter(p => p.status === 'COMPLETED').map((p: any) => (
                        <tr key={p.paymentMethod}>
                          <td style={{ fontWeight: 500 }}>{p.paymentMethod}</td>
                          <td>{p.count}</td>
                          <td>{parseFloat(p.total || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
            {/* Daily trend (last 30 days) */}
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Daily Shipments (Last 30 Days)</h2>
              {dailyTrend.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No data</div> : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 100, overflowX: 'auto' }}>
                  {dailyTrend.map((d: any) => (
                    <div key={d.date} title={`${d.date}: ${d.count}`} style={{ flex: '0 0 auto', width: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <div style={{
                        width: '100%',
                        height: `${(parseInt(d.count) / maxDaily) * 80}px`,
                        minHeight: parseInt(d.count) > 0 ? 3 : 0,
                        background: '#3b82f6',
                        borderRadius: '2px 2px 0 0',
                      }} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top destination cities */}
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Top Destinations</h2>
              {topRoutes.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No data</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {topRoutes.map((r: any, i: number) => (
                    <div key={r.receiverCity || i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ fontWeight: 500 }}>{r.receiverCity || '—'}</span>
                      <span className="badge badge-inactive">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
